import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, KeyRound, UserPlus } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } })
};

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export default function AdminLogin() {
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [setupComplete, setSetupComplete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/auth/status`).then(r => setSetupComplete(r.data.setup_complete)).catch(() => setSetupComplete(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.setup_required) {
        sessionStorage.setItem("tripsi_token", data.token);
        setStep("setup_account");
      } else if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("mfa");
      } else {
        sessionStorage.setItem("tripsi_token", data.token);
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/verify-mfa-login`, { code: mfaCode }, {
        headers: { "X-MFA-Token": mfaToken }
      });
      sessionStorage.setItem("tripsi_token", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid MFA code.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("tripsi_token");
      const { data } = await axios.post(`${API}/auth/initial-setup`, { email: newEmail, password: newPassword, name: newName }, authHeaders(token));
      sessionStorage.setItem("tripsi_token", data.token);
      // Start MFA setup
      const mfaResp = await axios.post(`${API}/auth/setup-mfa`, {}, authHeaders(data.token));
      setQrCode(mfaResp.data.qr_code);
      setMfaSecret(mfaResp.data.secret);
      setStep("setup_mfa");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("tripsi_token");
      await axios.post(`${API}/auth/confirm-mfa`, { code: mfaCode }, authHeaders(token));
      setSuccess("MFA enabled! Redirecting to dashboard...");
      setTimeout(() => navigate("/admin/dashboard"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (setupComplete === null) return null;

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center py-24">
      <div className="w-full max-w-md px-6">
        <motion.div initial="hidden" animate="visible" className="bg-[#141414] border border-white/5 p-8 md:p-10">

          {step === "login" && (
            <>
              <motion.div variants={fadeUp} custom={0} className="text-center mb-8">
                <div className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock size={24} className="text-[#E60000]" />
                </div>
                <h1 className="font-['Outfit'] text-2xl font-bold text-white">Admin Login</h1>
                <p className="text-[#A3A3A3] text-sm mt-2">
                  {setupComplete ? "Sign in with your admin account" : "Sign in with the default credentials to set up your account"}
                </p>
              </motion.div>
              <motion.form variants={fadeUp} custom={1} onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Email</label>
                  <Input data-testid="admin-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={setupComplete ? "your@email.com" : "admin@tripsi.com"} className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12" />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Password</label>
                  <Input data-testid="admin-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12" />
                </div>
                {error && <p data-testid="admin-login-error" className="text-[#E60000] text-sm">{error}</p>}
                <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-12 font-semibold">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </motion.form>
            </>
          )}

          {step === "setup_account" && (
            <>
              <motion.div variants={fadeUp} custom={0} className="text-center mb-8">
                <div className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4">
                  <UserPlus size={24} className="text-[#E60000]" />
                </div>
                <h1 className="font-['Outfit'] text-2xl font-bold text-white">Create Your Account</h1>
                <p className="text-[#A3A3A3] text-sm mt-2">Set up your permanent admin account. This can only be done once.</p>
              </motion.div>
              <motion.form variants={fadeUp} custom={1} onSubmit={handleCreateAccount} className="space-y-5">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Full Name</label>
                  <Input data-testid="setup-name" required value={newName} onChange={e => setNewName(e.target.value)} placeholder="Your full name" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12" />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Email</label>
                  <Input data-testid="setup-email" type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="your@email.com" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12" />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Password</label>
                  <Input data-testid="setup-password" type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Choose a strong password" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12" />
                </div>
                {error && <p className="text-[#E60000] text-sm">{error}</p>}
                <Button type="submit" data-testid="setup-account-btn" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-12 font-semibold">
                  {loading ? "Creating..." : "Create Account & Setup MFA"}
                </Button>
              </motion.form>
            </>
          )}

          {step === "setup_mfa" && (
            <>
              <motion.div variants={fadeUp} custom={0} className="text-center mb-6">
                <div className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4">
                  <KeyRound size={24} className="text-[#E60000]" />
                </div>
                <h1 className="font-['Outfit'] text-2xl font-bold text-white">Setup Authenticator</h1>
                <p className="text-[#A3A3A3] text-sm mt-2">Scan this QR code with Google Authenticator</p>
              </motion.div>
              <motion.div variants={fadeUp} custom={1} className="space-y-5">
                {qrCode && (
                  <div className="bg-white p-4 mx-auto w-fit">
                    <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
                  </div>
                )}
                {mfaSecret && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-3">
                    <p className="text-[#A3A3A3] text-xs mb-1">Manual entry key:</p>
                    <p className="text-white font-mono text-sm break-all select-all" data-testid="mfa-secret-key">{mfaSecret}</p>
                  </div>
                )}
                <form onSubmit={handleConfirmMfa} className="space-y-4">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Enter the 6-digit code to verify</label>
                    <Input data-testid="mfa-confirm-code" type="text" required maxLength={6} pattern="[0-9]{6}" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12 text-center text-2xl tracking-[0.5em] font-mono" />
                  </div>
                  {error && <p className="text-[#E60000] text-sm">{error}</p>}
                  {success && <p className="text-green-500 text-sm font-semibold">{success}</p>}
                  <Button type="submit" data-testid="mfa-confirm-btn" disabled={loading || !!success} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-12 font-semibold">
                    {loading ? "Verifying..." : "Enable MFA & Complete Setup"}
                  </Button>
                </form>
              </motion.div>
            </>
          )}

          {step === "mfa" && (
            <>
              <motion.div variants={fadeUp} custom={0} className="text-center mb-8">
                <div className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={24} className="text-[#E60000]" />
                </div>
                <h1 className="font-['Outfit'] text-2xl font-bold text-white">Two-Factor Authentication</h1>
                <p className="text-[#A3A3A3] text-sm mt-2">Enter the code from your authenticator app</p>
              </motion.div>
              <motion.form variants={fadeUp} custom={1} onSubmit={handleMfaVerify} className="space-y-5">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">6-Digit Code</label>
                  <Input data-testid="mfa-code-input" type="text" required maxLength={6} pattern="[0-9]{6}" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#E60000]/50 h-12 text-center text-2xl tracking-[0.5em] font-mono" />
                </div>
                {error && <p className="text-[#E60000] text-sm">{error}</p>}
                <Button type="submit" data-testid="mfa-verify-btn" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-12 font-semibold">
                  {loading ? "Verifying..." : "Verify & Sign In"}
                </Button>
                <button type="button" onClick={() => { setStep("login"); setMfaCode(""); setError(""); }} className="w-full text-[#A3A3A3] hover:text-white text-sm transition-colors">
                  Back to login
                </button>
              </motion.form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
